import React from 'react';
import { formatDate, referredBy } from '../api';
import { RATING_LABELS } from './RatingRow';

/**
 * One label-over-value cell. It takes a whole column of the .jm-facts grid —
 * two for a wide one — so the labels stay on the same vertical lines down the
 * whole block.
 */
const Fact = ({ label, value, wide = false }) => (
  <div className={`mb-2${wide ? ' jm-fact-wide' : ''}`} style={{ minWidth: 0 }}>
    <div
      className="text-muted text-uppercase"
      style={{ fontSize: '0.65rem', letterSpacing: '0.04em' }}
    >
      {label}
    </div>
    <div style={{ fontSize: '0.82rem', fontWeight: 500, wordBreak: 'break-word' }}>
      {value || '—'}
    </div>
  </div>
);

/**
 * The candidate's details at the top of every stage dialog. A grid rather than
 * a table, so nothing scrolls sideways inside the modal.
 */
export const CandidateSummary = ({ candidate }) => {
  if (!candidate) return null;

  const salary = `${candidate.current_salary || '—'} / ${candidate.expected_salary || '—'}`;

  return (
    <div className="border rounded px-3 pt-2 pb-1 mb-3" style={{ background: '#fafbfc' }}>
      <div className="jm-facts">
        <Fact label="Date of Birth" value={formatDate(candidate.dob)} />
        <Fact label="Gender" value={candidate.gender} />
        <Fact label="Marital Status" value={candidate.marriage_status} />
        <Fact label="Curr / Exp Salary" value={salary} />
        <Fact label="Mobile" value={candidate.contact_no} />
        <Fact label="Experience" value={candidate.experience} />
        <Fact label="Email" value={candidate.email} wide />
        <Fact label="Qualification" value={candidate.qualification} wide />
        <Fact label="Referred By" value={referredBy(candidate)} />
        <Fact label="Applied On" value={formatDate(candidate.created_at)} />
        {candidate.address ? <Fact label="Address" value={candidate.address} wide /> : null}
      </div>
    </div>
  );
};

/** One scored criterion, shown as a small stat rather than a table column. */
const ScoreChip = ({ label, value }) => {
  const scored = value !== null && value !== undefined && value !== '';
  const name = scored ? RATING_LABELS[Number(value)] : null;

  return (
    <div className="mb-2" style={{ minWidth: 0 }}>
      <div
        className="text-muted text-uppercase"
        style={{ fontSize: '0.63rem', letterSpacing: '0.04em' }}
      >
        {label}
      </div>
      <div style={{ fontSize: '0.8rem', fontWeight: 500 }}>
        {name ? `${name} (${value})` : '—'}
      </div>
    </div>
  );
};

/**
 * A recap of an earlier stage's scores, so the interviewer can see what the
 * previous round concluded without leaving the dialog.
 */
export const ScoreTable = ({ title, fields, row, total }) => {
  if (!row) return null;

  const max = fields.length * 5;

  return (
    <details className="border rounded mb-3" open>
      <summary
        className="d-flex justify-content-between align-items-center px-3 py-2"
        style={{ cursor: 'pointer', listStyle: 'none', background: '#f4f6f8' }}
      >
        <span style={{ fontWeight: 600, fontSize: '0.86rem' }}>{title}</span>
        <span className="badge badge-secondary">Score: {total} / {max}</span>
      </summary>

      <div className="px-3 pt-2 pb-1">
        <div className="jm-facts">
          {fields.map((field) => (
            <ScoreChip key={field.key} label={field.label} value={row[field.key]} />
          ))}
        </div>
        {row.notes ? (
          <div className="text-muted pb-2" style={{ fontSize: '0.78rem' }}>
            <strong>Remark:</strong> {row.notes}
          </div>
        ) : null}
      </div>
    </details>
  );
};

/**
 * The heading of a block the dialog is editing — the stage name on the left and,
 * where the block is scored, a live total on the right.
 */
export const SectionHeader = ({ title, score, max }) => (
  <div
    className="d-flex justify-content-between align-items-center mb-3 pb-2"
    style={{ borderBottom: '2px solid #009879' }}
  >
    <span style={{ fontWeight: 600, color: '#009879', fontSize: '0.95rem' }}>{title}</span>
    {score !== undefined && (
      <span className="badge badge-success" style={{ fontSize: '0.78rem' }}>
        Score: {score} / {max}
      </span>
    )}
  </div>
);

export const PHONE_SCORE_FIELDS = [
  { key: 'communication', label: 'Communication' },
  { key: 'Reason', label: 'Reason for Interest' },
  { key: 'Stabilily', label: 'Job Stability' },
  { key: 'Education', label: 'Education / Training' },
  { key: 'Accomplishment', label: 'Accomplishment' },
];

export const DEMO_SCORE_FIELDS = [
  { key: 'content', label: 'Content' },
  { key: 'professional', label: 'Professional' },
  { key: 'properties', label: 'Properties' },
  { key: 'voice', label: 'Voice' },
];

export const PERSONAL_SCORE_FIELDS = [
  { key: 'grooming', label: 'Grooming' },
  { key: 'body_language', label: 'Body Language' },
  { key: 'ability_learn', label: 'Ability to Learn' },
  { key: 'communication', label: 'Communication' },
  { key: 'team_player', label: 'Team Player' },
  { key: 'Responsible', label: 'Responsible' },
  { key: 'Dedicated', label: 'Dedicated' },
];

export default CandidateSummary;
